import React from 'react';
import styled from 'astroturf/react';

import { HeroThumbnailStack } from 'js/liswp_blocks/shared/HeroThumbnailStack';
import { PdfMiniChip } from 'js/liswp_blocks/shared/PdfMiniChip';
import { formatStat } from 'js/liswp_blocks/shared/formatStat';

const Card = styled.div<{ fill?: 'lightblue' }>`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;
  display: flex;
  gap: var(--spacing-xs-16, 16px);
  align-items: center;
  width: 100%;
  padding: var(--spacing-sm-24, 24px) 20px var(--spacing-xs-16, 16px);
  border: 2px solid #2a5dc1;
  border-radius: var(--border-radius-radius-md, 8px);
  text-align: left;

  &.fill-lightblue {
    background: var(--background-light-blue, #eef2f9);
  }

  @include breakpoint(small, medium) {
    align-items: flex-start;
  }
`;

const Flag = styled.div`
  position: absolute;
  top: -16px;
  left: var(--spacing-xs-16, 16px);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-xs-4, 4px) var(--spacing-xs-8, 8px);
  border-radius: 7px;
  background: var(--primary-brand, #0645b1);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-sm-bold-size, 14px);
  line-height: var(--type-body-sm-bold-line-height, 20px);
  font-weight: var(--type-body-sm-bold-font-weight, 600);
  color: var(--text-white, #ffffff);
  white-space: nowrap;
`;

const Thumb = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 64px;
  height: 86.416px;
`;

const Info = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;
`;

const Title = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
  padding-bottom: var(--spacing-xs-4, 4px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-md-bold-size, 16px);
  line-height: var(--type-body-md-bold-line-height, 20px);
  font-weight: var(--type-body-md-bold-font-weight, 600);
  color: var(--text-primary, #141413);
`;

const MetaLine = styled.p`
  margin: 0;
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-sm-size, 14px);
  line-height: var(--type-body-sm-line-height, 20px);
  color: var(--text-primary, #141413);
`;

export type DownloadHeroCardProps = {
  mode: 'pdf_only' | 'pdf_pack';
  workTitle: string;
  authorLine?: string;
  publicationLine?: string;
  thumbnailUrl?: string;
  relatedCount?: number;
};

export const DownloadHeroCard: React.FC<DownloadHeroCardProps> = ({
  mode,
  workTitle,
  authorLine,
  publicationLine,
  thumbnailUrl,
  relatedCount = 0,
}) => (
  <Card fill={mode === 'pdf_only' ? 'lightblue' : undefined}>
    <Flag>
      {mode === 'pdf_pack'
        ? `Get this PDF + ${formatStat(relatedCount)} PDFs`
        : 'Get this free PDF'}
    </Flag>
    <Thumb>
      <HeroThumbnailStack
        thumbnailUrl={thumbnailUrl}
        pageBorderColor="var(--stroke-beige-light, #ccc9bc)"
      />
      <PdfMiniChip size="modalcard" />
    </Thumb>
    <Info>
      <Title>{workTitle}</Title>
      {authorLine && <MetaLine>{authorLine}</MetaLine>}
      {publicationLine && <MetaLine>{publicationLine}</MetaLine>}
    </Info>
  </Card>
);

export default DownloadHeroCard;
